/**
 * Extended-warranty selector.
 *
 * Radios give mutual exclusivity for free, but they cannot be unchecked by clicking — and here
 * "no selection" is a real, meaningful state: it means the customer declined, and no warranty
 * line is added. So a click on the already-selected option clears it.
 *
 * Two details make this harder than it looks, and both are visible in an event trace:
 *
 * 1. The previous state has to be captured before the browser applies its own selection —
 *    by `click` the radio is already checked and what came before is gone. Hence `mousedown`.
 *
 * 2. A `<label>` wrapping its `<input>` produces TWO click events per tap: one on whatever was
 *    physically hit, then a forwarded activation click on the input itself. Clearing on the
 *    first one is undone microseconds later when the browser re-checks the radio on the second:
 *
 *      mousedown tgt=SPAN   checked=1   <- record
 *      click     tgt=SPAN   checked=1   <- clear here and it looks like it worked...
 *      change               checked=0
 *      click     tgt=INPUT  checked=1   <- ...until the label's own activation re-checks it
 *
 *    So the clear must happen on the input's click, which is the last event in the chain and
 *    therefore the only one nothing can undo.
 */
class WarrantyPlans extends HTMLElement {
  connectedCallback() {
    this.addEventListener('mousedown', this.#remember);
    this.addEventListener('keydown', this.#rememberKey);
    this.addEventListener('click', this.#maybeClear);
  }

  disconnectedCallback() {
    this.removeEventListener('mousedown', this.#remember);
    this.removeEventListener('keydown', this.#rememberKey);
    this.removeEventListener('click', this.#maybeClear);
  }

  /** @type {boolean} */
  #wasChecked = false;

  #remember = (event) => {
    const input = /** @type {HTMLElement} */ (event.target).closest('label')?.querySelector('input');
    this.#wasChecked = input instanceof HTMLInputElement && input.checked;
  };

  /**
   * Keyboard toggle-off.
   *
   * A radio that is already checked fires NO click when Space is pressed on it — the browser
   * treats it as a no-op, so the click-based clear above never runs. The trace is unambiguous:
   *
   *   Space on unchecked : keydown -> keyup -> click -> input -> change   (native selects it)
   *   Space on checked   : keydown -> keyup                              (nothing else)
   *
   * So the clear has to happen here, on keydown, where the pre-press state is still readable.
   * When the radio is unchecked the native path is left completely alone.
   */
  #rememberKey = (event) => {
    if (event.key !== ' ' && event.key !== 'Enter') return;

    const input = document.activeElement;
    if (!(input instanceof HTMLInputElement) || !this.contains(input)) return;

    this.#wasChecked = input.checked;
    if (!input.checked) return;

    // Already selected: this press means "remove it". Stop the browser doing its own thing,
    // then clear and announce it exactly as a mouse clear would.
    event.preventDefault();
    input.checked = false;
    this.#wasChecked = false;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  };

  #maybeClear = (event) => {
    // Only the input's own click is final. Every earlier click in the same tap is followed by
    // the label forwarding activation to the control, which would re-check what we just cleared.
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !this.#wasChecked) return;
    input.checked = false;
    this.#wasChecked = false;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  };
}

if (!customElements.get('warranty-plans')) {
  customElements.define('warranty-plans', WarrantyPlans);
}
